import axiosInstance from "@/lib/axiosInstance";
import { PLATFORM_TENANT_ID } from "@/store/entityTenantStore";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import type {
  UserHeldRole,
  UserHeldRolesResponse,
  UserScopeCompany,
  UserScopeResponse,
} from "@/api/access/userScope.types";
import { userStatusOf } from "@/api/user-management/usersDirectory.live";
import type { HolderEntity, RoleHolder, RoleHolderPerson } from "./roleHolders.types";

// The holders read itself lives with the other role reads (`fetchRoleHolders`, SERV-3393). These
// per-person reads serve the person record and the role page's Set Scope walk-through; the Users
// tab reads everything it shows from the holders row (SERV-3577, UI7).
export const roleHoldersEndpoints = {
  userScope: (userId: string) => `/access/users/${userId}/scope`,
  userRoles: (userId: string) => `/access/users/${userId}/roles`,
};

/** `actingEntityId` overrides the request's company; the platform company sends none. */
export const getUserScope = async (
  userId: string,
  signal?: AbortSignal,
  actingEntityId?: string,
): Promise<UserScopeCompany[]> => {
  const { data } = await axiosInstance.get<UserScopeResponse>(
    roleHoldersEndpoints.userScope(userId),
    { signal, suppressErrorAlert: true, actingEntityId } as CustomAxiosRequestConfig,
  );
  return data.data ?? [];
};

export const getUserHeldRoles = async (
  userId: string,
  signal?: AbortSignal,
): Promise<UserHeldRole[]> => {
  const { data } = await axiosInstance.get<UserHeldRolesResponse>(
    roleHoldersEndpoints.userRoles(userId),
    { signal, suppressErrorAlert: true } as CustomAxiosRequestConfig,
  );
  return data.data ?? [];
};

// A person holds the role until the last of their rows ends, not the first.
const latest = (a: string | null, b: string | null): string | null => {
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
};

/**
 * The row's own words where it has them. The holders read can return a null name, email or
 * company, so each falls back to the next thing that identifies the person rather than
 * rendering an empty cell or the word "null".
 */
const personName = (row: RoleHolder) => row.name?.trim() || row.email?.trim() || "Unnamed person";
const UNNAMED_ENTITY = "Unnamed entity";

const entityOf = (row: RoleHolder): HolderEntity => ({
  tenantId: row.tenantId,
  tenantName: row.tenantName ?? UNNAMED_ENTITY,
  temporary: row.temporary,
  roleId: row.roleId ?? null,
  scopeSet: typeof row.scopeSet === "boolean" ? row.scopeSet : null,
  // `null` from the read is "no live membership"; a row that leaves the field out says nothing.
  isMember: row.membershipStatus === undefined ? null : row.membershipStatus !== null,
});

/**
 * Folds the per-copy holder rows into one row per person, keeping every entity they hold the
 * role in. Rows arrive ordered by name, so a person's rows are adjacent, but this does not
 * rely on that.
 *
 * An ended temporary row grants nothing, so it is dropped before folding: it must not add an
 * entity the person has left.
 */
export const groupHoldersByPerson = (rows: RoleHolder[]): RoleHolderPerson[] => {
  const byUser = new Map<string, RoleHolderPerson>();

  for (const row of rows.filter((row) => !row.ended)) {
    const existing = byUser.get(row.userId);
    if (!existing) {
      byUser.set(row.userId, {
        userId: row.userId,
        name: personName(row),
        email: row.email ?? "",
        entities: [entityOf(row)],
        temporary: row.temporary,
        endsOn: row.endsOn,
        status: userStatusOf(row.userStatus),
        // The read lists one person's other roles once, on each of their rows.
        otherRoles: Array.isArray(row.otherRoles) ? row.otherRoles : null,
      });
      continue;
    }
    if (!existing.entities.some((entity) => entity.tenantId === row.tenantId)) {
      existing.entities.push(entityOf(row));
    }
    // Holding it permanently in one entity makes the person a permanent holder overall, even
    // though the temporary copy elsewhere still cannot be removed from here.
    existing.temporary = existing.temporary && row.temporary;
    existing.endsOn = existing.temporary ? latest(existing.endsOn, row.endsOn) : null;
  }

  return [...byUser.values()];
};

/**
 * What a holder's Scope cell is saying, worked out once so the table, the export, the Alert and
 * the Set Scope queue can never drift apart. One unset copy is enough: the engine gives the person
 * nothing in a company with an empty required box. `unknown` shows nothing, never a warning.
 */
export type HolderScopeState = { kind: "not_set" } | { kind: "set" } | { kind: "unknown" };

export const holderScopeState = (entities: Pick<HolderEntity, "scopeSet">[]): HolderScopeState => {
  if (entities.some((entity) => entity.scopeSet === false)) return { kind: "not_set" };
  if (entities.length > 0 && entities.every((entity) => entity.scopeSet === true)) {
    return { kind: "set" };
  }
  return { kind: "unknown" };
};

/**
 * How many holders the scope Alert may claim, and whether that is a floor: the holder list was
 * truncated at the page cap, or the read could not judge someone, who could be one more.
 */
export const scopeAlertCount = (input: {
  rows: Array<Pick<RoleHolderPerson, "entities">>;
  /** False when the holder list itself was truncated at the page cap. */
  everyHolderRead: boolean;
}): { needsScopeCount: number; needsScopeIsPartial: boolean } => {
  const states = input.rows.map((row) => holderScopeState(row.entities).kind);
  return {
    needsScopeCount: states.filter((kind) => kind === "not_set").length,
    needsScopeIsPartial: !input.everyHolderRead || states.includes("unknown"),
  };
};

// SERV-3577 (UI7): the Scope column's per-company summary ("4 clients · 2 trades") is off because
// the per-person scope read it needs (getUserScope per holder) is gone from the Users tab. Restore
// with that read, HolderScopeEntity in roleHolders.types.ts and the tests in roleHolders.test.ts.
//
// import type { ScopeBox } from "@/api/access/access.types";
// import type { HolderScopeEntity } from "./roleHolders.types";
//
// const CLIENT_SUMMARY: Record<string, (count: number) => string> = {
//   all: () => "All clients",
//   all_except: (count) => (count === 1 ? "All clients except 1" : `All clients except ${count}`),
//   only: (count) => (count === 1 ? "1 client" : `${count} clients`),
// };
//
// const CATEGORY_BOX_LABEL: Partial<Record<ScopeBox, string>> = {
//   brand: "Brands",
//   priority: "Priorities",
//   site: "Sites",
//   work_order_type: "Work order types",
//   trade: "Trades",
//   subtrade: "Subtrades",
//   state: "Areas",
// };
//
// /**
//  * The one-line summary the Scope column shows for an entity: what narrows the person there.
//  *
//  * Only boxes that actually narrow them are named. A rule kept for a box no role uses is stored
//  * but not applied (backend D25), and an in-play box left empty narrows nothing either — the
//  * engine reads "only empty optional boxes" as reaching everything. An empty summary therefore
//  * means nothing narrows them, which is not the same as nothing being set.
//  */
// export const summariseScope = (company: UserScopeCompany): string => {
//   const parts: string[] = [];
//
//   const clientsInPlay = company.boxes.some(
//     (entry) => entry.box === "clients" && entry.kind !== "not_in_play",
//   );
//   const effective = company.clients.effective;
//   const clientsEmpty = effective.kind === "only" && effective.clientIds.length === 0;
//   if (clientsInPlay && !clientsEmpty) {
//     const count = effective.kind === "all" ? 0 : effective.clientIds.length;
//     parts.push(CLIENT_SUMMARY[effective.kind](count));
//   }
//
//   for (const [box, rule] of Object.entries(company.rules)) {
//     if (!rule?.applied || rule.ids.length === 0) continue;
//     const label = CATEGORY_BOX_LABEL[box as ScopeBox] ?? box;
//     parts.push(
//       rule.type === "exclusion"
//         ? `${label} except ${rule.ids.length}`
//         : `${rule.ids.length} ${label.toLowerCase()}`,
//     );
//   }
//
//   return parts.join(" · ");
// };
//
// /**
//  * The person's scope in the entities where they hold this role. Entities the scope response
//  * does not cover are left out rather than reported empty: not being told is not "nothing set".
//  */
// export const holderScopeIn = (
//   companies: UserScopeCompany[],
//   entityIds: string[],
// ): HolderScopeEntity[] =>
//   companies
//     .filter((company) => entityIds.includes(company.tenantId))
//     .map((company) => ({
//       tenantId: company.tenantId,
//       tenantName: company.tenantName,
//       boxes: company.boxes
//         .filter((entry) => entry.kind !== "not_in_play")
//         .map((entry) => ({
//           box: entry.box,
//           required: entry.required,
//           emptyRequired: entry.emptyRequired,
//         })),
//       summary: summariseScope(company),
//     }));
//
// /**
//  * No box in play is not an empty scope: the engine narrows nothing, so the person reaches
//  * everything in that entity. Saying "—" here would read as the opposite of what is true.
//  */
// const NOTHING_NARROWS = "Everything in this entity";
//
// /** What the Scope cell reads for a holder whose scope is known and whose required boxes are filled. */
// export const scopeSummaryText = (scope: HolderScopeEntity[]): string => {
//   const named = scope.length > 1;
//   return scope
//     .map((entity) => {
//       const text = entity.summary || NOTHING_NARROWS;
//       return named ? `${entity.tenantName}: ${text}` : text;
//     })
//     .join(" · ");
// };

/** The role whose record we are on, as the holders endpoint understands it: every copy of a code. */
export type ThisRole = { id: string; code: string | null; name: string; tenantId: string | null };

/** One copy Remove takes away, and the company its write acts in. */
export type HolderCopy = { roleId: string; tenantId: string };

type HoldingFields = Pick<HolderEntity, "tenantId" | "tenantName" | "temporary" | "roleId">;

/**
 * The holdings this record answers for: every company's on a ServEngine role (the platform copy of
 * a `both` role applies in each company the person belongs to, D51), otherwise this company's.
 */
const holdingsHere = <T extends HoldingFields>(entities: T[], roleTenantId: string | null): T[] =>
  roleTenantId === null || roleTenantId === PLATFORM_TENANT_ID
    ? entities
    : entities.filter((entity) => entity.tenantId === roleTenantId);

/**
 * What Remove writes: one `removedRoleIds` per copy the person holds here permanently, acting in
 * that row's company (D251). The backend soft-deletes only rows of the id it is sent with
 * `expires_at IS NULL`, so the record's own id cannot touch another copy (SERV-3520 put the copy
 * on each row); a row from an older backend falls back to the record's id.
 */
export const removableCopies = (
  entities: HoldingFields[],
  role: Pick<ThisRole, "id" | "tenantId">,
): HolderCopy[] => {
  const copies: HolderCopy[] = [];
  for (const entity of holdingsHere(entities, role.tenantId)) {
    if (entity.temporary) continue;
    const roleId = entity.roleId ?? role.id;
    if (!copies.some((copy) => copy.roleId === roleId)) {
      copies.push({ roleId, tenantId: entity.tenantId });
    }
  }
  return copies;
};

/** Why Remove cannot act on a person, when it cannot. Offering it anyway reports a no-op as done. */
export type RemoveBlock =
  | { kind: "allowed" }
  /** They hold a different entity's copy, which this record's id cannot touch. */
  | { kind: "other_entity"; entityName: string }
  /** This copy is temporary, and the write only deletes rows with no expiry. */
  | { kind: "temporary" };

export const removeBlock = (
  entities: HoldingFields[],
  roleTenantId: string | null,
): RemoveBlock => {
  const here = holdingsHere(entities, roleTenantId);
  if (here.some((entity) => !entity.temporary)) return { kind: "allowed" };
  if (here.length > 0) return { kind: "temporary" };
  return { kind: "other_entity", entityName: entities[0]?.tenantName ?? "another entity" };
};

/**
 * The roles the person still holds once Remove takes this record's copies: their other roles, and
 * this one while any copy of it is left (another company's, or a temporary one Remove cannot take),
 * so someone who keeps it elsewhere is not warned that they are about to lose all access.
 */
export const keptAfterRemoving = (
  person: Pick<RoleHolderPerson, "entities" | "otherRoles">,
  role: ThisRole,
): string[] | null => {
  if (person.otherRoles === null) return null;
  const removed = new Set(removableCopies(person.entities, role).map((copy) => copy.roleId));
  const keepsThisRole = person.entities.some(
    (entity) => entity.temporary || !removed.has(entity.roleId ?? role.id),
  );
  const kept = [...person.otherRoles];
  if (keepsThisRole && !kept.includes(role.name)) kept.push(role.name);
  return kept;
};
